/**
 * WavePopup.js
 * Implements the wave announcement animation
 */

import { CoreEvents, Game, GameTween, GameWindow, UiElement, UiImage, UiText, WindowLayers } from "pixi_core";
import { Common } from "../game/Common";
import { ABILITY_NAME_ROTATION, GameEvents } from "../Constants";

export class WavePopup extends GameWindow {
    /**
     * @public
     */
    constructor() {
        super({
            name: "WavePopup",
            disableInputUnder: false,
            showDarkenUnder: false,
            ignoreOutsideZoneTap: true,
            ignoreGearButton: true,
            ignoreAndroidBackButton: true,
            defaultInstantClose: true,
            defaultInstantOpen: true,
            layer: WindowLayers.eUNDER_GEAR
        });

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {String} */
        this._waveText = "";

        /** @private @type {UiImage} */
        this._waveImage = null;

        /** @private @type {UiText} */
        this._lblWave = null;

        /** @private @type {Number} */
        this._waveNumber = 0;

        /** @private @type {Number} */
        this._totalNumber = 0;

        /** @private @type {Object} */
        this._waveFinalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._waveMiddlePos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._waveStartingPos = { x: 0, y: 0 };

        /** @private @type {GameTween} */
        this._tweenWaveCutIn = null;

        /** @private @type {GameTween} */
        this._tweenWaveCutStop = null;

        /** @private @type {GameTween} */
        this._tweenWaveCutOut = null;

        Game.on(GameEvents.eGAME_START_WAVE, this._onStartWave, this);

        Game.on(CoreEvents.eLOCALE_CHANGED, this._onLocaleChanged, this);
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("WavePopup", this);

        this._waveImage = this._ui.findElementByName("ImgWave");
        this._lblWave = this._ui.findElementByName("LblWave");
        this._lblWave.uiRoot.angle = ABILITY_NAME_ROTATION;

        this._waveMiddlePos = { x: this._waveImage.x, y: this._waveImage.y };
        this._waveFinalPos = { x: this._waveImage.x + Game.screen.width, y: this._waveImage.y - Common.settings.cutInYDistance };
        this._waveStartingPos = { x: this._waveImage.x - Game.screen.width, y: this._waveImage.y + Common.settings.cutInYDistance };
    }

    /**
     * The window is opening
     * @protected
     * @param {Object} options
     */
    _onWindowOpening(options) {}

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {}

    /**
     * The window is closing
     * @protected
     */
    _onWindowClosing(options) {}

    /**
     * The window is closed
     * @protected
     */
    _onWindowClosed() {
        this._destroyCutInTweens();
    }

    /**
     * @private
     * @param {Number} waveNumber
     * @param {Number} totalNumber
     */
    _onStartWave(waveNumber, totalNumber) {
        this.open();
        this._waveImage.visible = true;
        this._waveNumber = waveNumber;
        this._totalNumber = totalNumber;

        Game.audio.playSfx("IDSND_WAVE_START");
        let waveString = " " + Game.localization.getText("IDS_TXT_WAVE_WITH_VALUE") + " ";
        waveString = waveString.replace("[%d]", this._waveNumber.toString());
        waveString = waveString.replace("[%d]", this._totalNumber.toString());

        this._lblWave.text = waveString;
        this._waveImage.setPosition(this._waveStartingPos.x, this._waveStartingPos.y);

        this._tweenWaveCutIn = this.createTween(this._waveImage)
            .to(
                {
                    x: this._waveMiddlePos.x - Common.settings.cutInStopDistanceX,
                    y: this._waveMiddlePos.y + Common.settings.cutInStopDistanceY
                },
                Common.settings.cutInMoveDuration
            )
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._tweenWaveCutStop.start();
            });

        this._tweenWaveCutStop = this.createTween(this._waveImage)
            .to(
                {
                    x: this._waveMiddlePos.x + Common.settings.cutInStopDistanceX,
                    y: this._waveMiddlePos.y - Common.settings.cutInStopDistanceY
                },
                Common.settings.cutInStopDuration
            )
            .easing(GameTween.Easing.Linear)
            .onComplete(() => {
                this._tweenWaveCutOut.start();
            });

        this._tweenWaveCutOut = this.createTween(this._waveImage)
            .to({ x: this._waveFinalPos.x, y: this._waveFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticIn)
            .onComplete(() => {
                this._onCutInComplete();
            });

        this._tweenWaveCutIn.start();
    }

    /**
     * @private
     */
    _onCutInComplete() {
        this._waveImage.visible = false;
        this._destroyCutInTweens();
        Game.fire(GameEvents.eGAME_START_WAVE_COMPLETED);
        this.close();
    }

    /**
     * A locale change occurred
     * @private
     * @param {String} localeId
     * @param {Boolean} rtlLocale
     */
    _onLocaleChanged(localeId, rtlLocale) {
        if (!this.isOpened) return;

        let waveString = " " + Game.localization.getText("IDS_TXT_WAVE_WITH_VALUE") + " ";
        waveString = waveString.replace("[%d]", this._waveNumber.toString());
        waveString = waveString.replace("[%d]", this._totalNumber.toString());

        this._lblWave.text = waveString;
    }

    /**
     * @private
     */
    _destroyCutInTweens() {
        if (this._tweenWaveCutIn) {
            this._tweenWaveCutIn.destroy();
            this._tweenWaveCutIn = null;
        }

        if (this._tweenWaveCutStop) {
            this._tweenWaveCutStop.destroy();
            this._tweenWaveCutStop = null;
        }

        if (this._tweenWaveCutOut) {
            this._tweenWaveCutOut.destroy();
            this._tweenWaveCutOut = null;
        }
    }
}
